import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Mail, Lock, User, Key, ArrowRight, ShieldCheck,
  Loader2, Eye, EyeOff, CheckCircle2, Globe
} from 'lucide-react';
import './AdminAuth.css';

const AdminAuth = () => {
  const [view, setView] = useState('login');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const navigate = useNavigate();

  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Register state
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regSecretKey, setRegSecretKey] = useState('');

  // Live time
  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Mouse parallax
  useEffect(() => {
    const onMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth - 0.5) * 2,
        y: (e.clientY / window.innerHeight - 0.5) * 2,
      });
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/admin/login`, {
        email: loginEmail,
        password: loginPassword
      });
      localStorage.setItem('adminToken', res.data.token);
      localStorage.setItem('adminName', res.data.name);
      setTimeout(() => {
        setIsLoading(false);
        navigate('/admin-dashboard');
      }, 900);
    } catch (error) {
      setIsLoading(false);
      alert(error.response?.data?.message || 'Login Failed. Check credentials.');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/admin/register`, {
        name: regName,
        email: regEmail,
        password: regPassword,
        secretKey: regSecretKey
      });
      setTimeout(() => {
        setIsLoading(false);
        alert('Admin Registration Successful! You can now login.');
        setView('login');
      }, 900);
    } catch (error) {
      setIsLoading(false);
      alert(error.response?.data?.message || 'Registration Failed.');
    }
  };

  const formatTime = (date) =>
    date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });

  const formatDate = (date) =>
    date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  const meta = view === 'login'
    ? { title: 'Admin Portal', sub: 'Authenticate to access the command suite' }
    : { title: 'Request Access', sub: 'Submit credentials for verification' };

  return (
    <div className="adm-auth">
      <div
        className="adm-bg-image"
        style={{ transform: `translate3d(${mousePos.x * -10}px, ${mousePos.y * -6}px, 0) scale(1.05)` }}
      />
      <div className="adm-bg-overlay" />
      <div className="adm-bg-vignette" />
      <div
        className="adm-bg-mesh adm-mesh-1"
        style={{ transform: `translate3d(${mousePos.x * 20}px, ${mousePos.y * 20}px, 0)` }}
      />
      <div
        className="adm-bg-mesh adm-mesh-2"
        style={{ transform: `translate3d(${mousePos.x * -18}px, ${mousePos.y * -18}px, 0)` }}
      />

      <div className="adm-top-bar">
        <div className="adm-status">
          <span className="adm-pulse-dot" />
          <span>SECURE SESSION</span>
        </div>
        <div className="adm-top-meta">
          <span className="adm-time">{formatTime(currentTime)}</span>
          <span className="adm-dot">·</span>
          <span>{formatDate(currentTime)}</span>
          <span className="adm-dot">·</span>
          <span className="adm-region"><Globe size={11} /> DXB</span>
        </div>
      </div>

      <div className="adm-content">
        <div className="adm-brand-col">
          <div className="adm-brand-mark">
            <div className="adm-brand-icon">
              <ShieldCheck size={20} strokeWidth={2} />
            </div>
            <div className="adm-brand-text">
              <span className="adm-brand-name">HOTELPLACEMENT</span>
              <span className="adm-brand-sub">Administration Suite</span>
            </div>
          </div>

          <h1 className="adm-headline">
            The Gateway to <br />
            <span className="adm-gold-text">Operational Excellence</span>
          </h1>

          <p className="adm-subtext">
            Orchestrate your hospitality ecosystem with enterprise-grade precision.
            Built for those who refuse to compromise on security or sophistication.
          </p>

          <div className="adm-foot-meta">
            <div className="adm-foot-item">
              <ShieldCheck size={13} />
              <span>Role-based access control</span>
            </div>
            <div className="adm-foot-item">
              <Lock size={13} />
              <span>End-to-end encryption</span>
            </div>
          </div>
        </div>

        <div className="adm-form-col">
          <div className="adm-panel">
            <div className="adm-panel-accent" />
            <div className="adm-panel-corner adm-c-tl" />
            <div className="adm-panel-corner adm-c-tr" />
            <div className="adm-panel-corner adm-c-bl" />
            <div className="adm-panel-corner adm-c-br" />

            <div className="adm-security-badge">
              <Lock size={10} />
              <span>TLS 1.3 SECURE</span>
            </div>

            <div className="adm-panel-header">
              <h2>{meta.title}</h2>
              <p>{meta.sub}</p>
            </div>

            <form onSubmit={view === 'login' ? handleLogin : handleRegister} className="adm-form">

              {view === 'register' && (
                <div className="adm-field adm-slide">
                  <label>Full Name</label>
                  <div className="adm-input-wrap">
                    <User className="adm-input-icon" size={16} />
                    <input
                      type="text"
                      placeholder="Your full name"
                      value={regName}
                      onChange={(e) => setRegName(e.target.value)}
                      required
                    />
                  </div>
                </div>
              )}

              <div className="adm-field">
                <label>Email Address</label>
                <div className="adm-input-wrap">
                  <Mail className="adm-input-icon" size={16} />
                  <input
                    type="email"
                    placeholder="admin@hotelplacement.ae"
                    value={view === 'login' ? loginEmail : regEmail}
                    onChange={(e) => view === 'login' ? setLoginEmail(e.target.value) : setRegEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="adm-field">
                <label>Password</label>
                <div className="adm-input-wrap">
                  <Lock className="adm-input-icon" size={16} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••••••"
                    value={view === 'login' ? loginPassword : regPassword}
                    onChange={(e) => view === 'login' ? setLoginPassword(e.target.value) : setRegPassword(e.target.value)}
                    required
                  />
                  <button type="button" className="adm-eye-btn"
                    onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              {view === 'register' && (
                <div className="adm-field adm-slide">
                  <label>Clearance Key</label>
                  <div className="adm-input-wrap">
                    <Key className="adm-input-icon" size={16} />
                    <input
                      type="password"
                      placeholder="IT-issued secret key"
                      value={regSecretKey}
                      onChange={(e) => setRegSecretKey(e.target.value)}
                      required
                    />
                  </div>
                </div>
              )}

              <button type="submit" className="adm-submit" disabled={isLoading}>
                <span className="adm-submit-bg" />
                <span className="adm-submit-content">
                  {isLoading ? (
                    <><Loader2 className="adm-spin" size={17} /><span>Verifying...</span></>
                  ) : (
                    <>
                      <span>{view === 'login' ? 'Enter Dashboard' : 'Submit Application'}</span>
                      <ArrowRight size={15} className="adm-submit-arrow" />
                    </>
                  )}
                </span>
              </button>

              <div className="adm-foot-note">
                <CheckCircle2 size={11} />
                <span>Protected by enterprise authentication</span>
              </div>
            </form>

            <div className="adm-toggle-row">
              <span>{view === 'login' ? "Need administrative access?" : "Already verified?"}</span>
              <button
                type="button"
                className="adm-toggle-link"
                onClick={() => setView(view === 'login' ? 'register' : 'login')}
              >
                {view === 'login' ? 'Request Access' : 'Return to Login'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAuth;